/**
 * Korean Hub cart helpers
 * - Safely initializes Shopify's country/province selector after deferred assets load.
 * - Handles empty or non-JSON shipping API errors without throwing.
 * - Keeps the theme's cart quantity and shipping-calculator behaviour.
 */
(function (window, document) {
  'use strict';

  var MAX_DEPENDENCY_ATTEMPTS = 30;
  var DEPENDENCY_RETRY_DELAY = 100;
  var MAX_RATE_POLL_ATTEMPTS = 40;
  var RATE_POLL_DELAY = 500;

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function route(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = typeof root === 'string' && root.length ? root : '/';
    return root.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
  }

  function safeJson(responseText) {
    if (!responseText || typeof responseText !== 'string') return {};

    try {
      return JSON.parse(responseText);
    } catch (error) {
      return {};
    }
  }

  function flattenErrors(errors) {
    var messages = [];

    if (!errors) return messages;
    if (typeof errors === 'string') return [errors];
    if (Array.isArray(errors)) {
      errors.forEach(function (item) {
        messages = messages.concat(flattenErrors(item));
      });
      return messages;
    }

    if (typeof errors === 'object') {
      Object.keys(errors).forEach(function (key) {
        flattenErrors(errors[key]).forEach(function (message) {
          messages.push(key + ' ' + message);
        });
      });
    }

    return messages;
  }

  function createShippingCalculator($) {
    var config = {
      submitButton: 'Calculate shipping',
      submitButtonDisabled: 'Calculating...',
      templateId: 'shipping-calculator-response-template',
      wrapperId: 'wrapper-response',
      customerIsLoggedIn: false,
      moneyFormat: '${{amount}}'
    };

    function enableButtons() {
      $('.get-rates')
        .prop('disabled', false)
        .removeClass('disabled')
        .val(config.submitButton);
    }

    function disableButtons() {
      $('.get-rates')
        .prop('disabled', true)
        .addClass('disabled')
        .val(config.submitButtonDisabled);
    }

    function formatRate(price) {
      var numericPrice = parseFloat(price);
      var cents = Number.isFinite(numericPrice) ? Math.round(numericPrice * 100) : 0;

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, config.moneyFormat);
      }

      return numericPrice.toFixed(2);
    }

    function render(data) {
      var templateElement = document.getElementById(config.templateId);
      var wrapperElement = document.getElementById(config.wrapperId);

      if (!templateElement || !wrapperElement || !window.Handlebars) return;

      if (!window.Handlebars.helpers.formatPrice) {
        window.Handlebars.registerHelper('formatPrice', function (value) {
          return value;
        });
      }

      var template = window.Handlebars.compile(templateElement.textContent.trim());
      wrapperElement.innerHTML = template(data);
      wrapperElement.style.display = '';

      if (window.Currency && typeof window.Currency.convertAll === 'function') {
        var currencySelector = document.querySelector('[name="currencies"]');
        var selectedCurrency = document.querySelector('#currencies span.selected');
        var targetCurrency = currencySelector
          ? currencySelector.value
          : selectedCurrency && selectedCurrency.getAttribute('data-currency');

        if (targetCurrency && window.shopCurrency) {
          window.Currency.convertAll(
            window.shopCurrency,
            targetCurrency,
            '#' + config.wrapperId + ' span.money, #estimated-shipping span.money'
          );
        }
      }
    }

    function showError(xhr) {
      var estimatedShipping = document.getElementById('estimated-shipping');
      var response = safeJson(xhr && xhr.responseText);
      var messages = flattenErrors(response.errors || response.description || response);
      var feedback = response.message || messages.join('; ') || 'Unable to calculate shipping for this destination. Please check the address and try again.';

      if (/country is not supported/i.test(feedback)) {
        feedback = 'We do not ship to this destination.';
      }

      if (estimatedShipping) estimatedShipping.style.display = 'none';
      enableButtons();
      render({ rates: [], errorFeedback: feedback, success: false });
    }

    function updateRates(rates, destination) {
      var address = [destination.zip, destination.province, destination.country]
        .filter(Boolean)
        .join(', ');
      var formattedRates = Array.isArray(rates)
        ? rates.map(function (rate) {
            return Object.assign({}, rate, { price: formatRate(rate.price) });
          })
        : [];
      var estimatedShipping = document.getElementById('estimated-shipping');

      enableButtons();

      if (estimatedShipping && formattedRates.length) {
        var amount = estimatedShipping.querySelector('em');
        if (amount) {
          amount.textContent = parseFloat(rates[0].price) === 0 ? 'FREE' : formattedRates[0].price;
        }
        estimatedShipping.style.display = '';
      }

      render({ rates: formattedRates, address: address, success: true });
    }

    function pollRates(destination, attempt) {
      $.ajax({
        url: route('cart/async_shipping_rates.json'),
        dataType: 'json',
        success: function (response, textStatus, xhr) {
          if (xhr.status === 200 && response && Array.isArray(response.shipping_rates)) {
            updateRates(response.shipping_rates, destination);
            return;
          }

          if (attempt < MAX_RATE_POLL_ATTEMPTS) {
            window.setTimeout(function () {
              pollRates(destination, attempt + 1);
            }, RATE_POLL_DELAY);
          } else {
            showError({ responseText: '' });
          }
        },
        error: function (xhr) {
          if (xhr && xhr.status === 202 && attempt < MAX_RATE_POLL_ATTEMPTS) {
            window.setTimeout(function () {
              pollRates(destination, attempt + 1);
            }, RATE_POLL_DELAY);
            return;
          }
          showError(xhr);
        }
      });
    }

    function requestRates(destination) {
      $.ajax({
        type: 'POST',
        url: route('cart/prepare_shipping_rates.json'),
        data: $.param({ shipping_address: destination }),
        success: function () {
          pollRates(destination, 0);
        },
        error: showError
      });
    }

    function initializeSelector() {
      var countryElement = document.getElementById('address_country');
      var provinceElement = document.getElementById('address_province');
      var labelElement = document.getElementById('address_province_label');

      if (!countryElement || !provinceElement) return false;

      new window.Shopify.CountryProvinceSelector(
        'address_country',
        'address_province',
        { hideElement: 'address_province_container' }
      );

      if (window.Countries && typeof window.Countries.updateProvinceLabel === 'function') {
        window.Countries.updateProvinceLabel(countryElement.value, labelElement);
        countryElement.addEventListener('change', function () {
          window.Countries.updateProvinceLabel(countryElement.value, labelElement);
        });
      }

      return true;
    }

    function initialize() {
      if (!initializeSelector()) return;

      $('.get-rates').off('click.khShipping').on('click.khShipping', function (event) {
        event.preventDefault();
        disableButtons();
        $('#' + config.wrapperId).empty().hide();

        requestRates({
          zip: $('#address_zip').val() || '',
          country: $('#address_country').val() || '',
          province: $('#address_province').val() || ''
        });
      });

      if (config.customerIsLoggedIn) {
        $('.get-rates').first().trigger('click');
      }
    }

    return {
      show: function (options) {
        config = $.extend(config, options || {});
        initialize();
      },
      formatRate: formatRate,
      getConfig: function () {
        return config;
      }
    };
  }

  function initializeShipping(attempt) {
    var calculator = document.getElementById('shipping-calculator');

    if (!calculator) return;

    var dependenciesReady =
      window.jQuery &&
      window.Handlebars &&
      window.Shopify &&
      typeof window.Shopify.CountryProvinceSelector === 'function';

    if (!dependenciesReady) {
      if (attempt < MAX_DEPENDENCY_ATTEMPTS) {
        window.setTimeout(function () {
          initializeShipping(attempt + 1);
        }, DEPENDENCY_RETRY_DELAY);
      }
      return;
    }

    window.Shopify.Cart = window.Shopify.Cart || {};
    window.Shopify.Cart.ShippingCalculator = createShippingCalculator(window.jQuery);

    var strings = window.theme && window.theme.strings ? window.theme.strings : {};
    window.Shopify.Cart.ShippingCalculator.show({
      submitButton: strings.shippingCalcSubmitButton || 'Calculate shipping',
      submitButtonDisabled: strings.shippingCalcSubmitButtonDisabled || 'Calculating...',
      customerIsLoggedIn: Boolean(strings.shippingCalcCustomerIsLoggedIn),
      moneyFormat: strings.shippingCalcMoneyFormat || '${{amount}}'
    });
  }

  function changeQuantity(button, amount) {
    var quantityWrapper = button.closest('.qty-box-set');
    var quantityInput = quantityWrapper && quantityWrapper.querySelector('input[name="updates[]"]');

    if (!quantityInput) return;

    var currentValue = parseInt(quantityInput.value, 10);
    currentValue = Number.isFinite(currentValue) ? currentValue : 1;
    quantityInput.value = Math.max(0, currentValue + amount);

    var updateButton = document.querySelector('input[name="update"]');
    if (updateButton) updateButton.click();
  }

  window.shippingCall = function () {
    var calculatorBlock = document.getElementById('shipping-calculator-block');
    if (!calculatorBlock) return;

    var shouldOpen = window.getComputedStyle(calculatorBlock).display === 'none';
    calculatorBlock.style.display = shouldOpen ? 'block' : 'none';

    var trigger = document.querySelector('#shipping-calculator > .dt-sc-btn');
    if (trigger) trigger.setAttribute('aria-expanded', String(shouldOpen));
  };

  onReady(function () {
    initializeShipping(0);

    document.addEventListener('click', function (event) {
      var plusButton = event.target.closest('.qtyplus1');
      var minusButton = event.target.closest('.qtyminus1');
      var cartHeading = event.target.closest('.cart_heading');

      if (plusButton) {
        event.preventDefault();
        changeQuantity(plusButton, 1);
      } else if (minusButton) {
        event.preventDefault();
        changeQuantity(minusButton, -1);
      }

      if (cartHeading) cartHeading.classList.toggle('clicked');
    });
  });
})(window, document);
